import { useEffect, useMemo, useState } from 'react';

import SubscriptionPreview from '@/pages/profile/channels/SubscriptionPreview';
import ChannelsService from '@/services/channels.service';
import ChannelSliderCard from '@/shared-components/cards/components/ChannelSliderCard';
import HomeHeader from '@/shared-components/home/components/HomeHeader';
import Loader from '@/shared-components/loader/Loader';
import StaticSeoHead from '@/shared-components/seo/StaticSeoHead';
import AppNavigationPage from '@/shared-components/wrappers/components/AppNavigationPage';

import {
  CHANNELS_DISCOVER_PATH,
  getChannelActivityTime,
  hydrateDiscoverChannel,
} from './utils/channelDiscovery';

import './styles/ChannelsDiscoverPage.css';

export default function ChannelsDiscoverPage() {
  const [channels, setChannels] = useState([]);
  const [previewChannel, setPreviewChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadChannels() {
      setLoading(true);
      setError('');

      try {
        const { response, data } = await ChannelsService.getAll();
        if (!response?.ok) {
          throw new Error('Unable to load channels.');
        }

        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        const hydratedChannels = await Promise.all(
          list.map((channel) => hydrateDiscoverChannel(channel))
        );

        const sorted = hydratedChannels
          .filter(Boolean)
          .sort((a, b) => getChannelActivityTime(b) - getChannelActivityTime(a));

        if (!cancelled) {
          setChannels(sorted);
        }
      } catch (nextError) {
        console.error('Error loading channels discover page:', nextError);
        if (!cancelled) {
          setChannels([]);
          setError('Unable to load channels right now.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadChannels();

    return () => {
      cancelled = true;
    };
  }, []);

  const hasChannels = useMemo(() => channels.length > 0, [channels]);

  return (
    <>
      <StaticSeoHead
        title="Channels | Suarte"
        description="Discover active VIP channels across Suarte, sorted by recent activity."
        canonicalPath={CHANNELS_DISCOVER_PATH}
        pageType="CollectionPage"
        breadcrumbItems={[
          { name: 'Home', url: 'https://suarte.art/' },
          { name: 'Channels', url: `https://suarte.art${CHANNELS_DISCOVER_PATH}` },
        ]}
      />

      <AppNavigationPage>
        <div className="channels-discover-page">
          <HomeHeader
            onInput={() => {}}
            value=""
            text="Channels"
            hideSearch
          />

          {loading ? (
            <div className="channels-discover-page__status">
              <Loader />
            </div>
          ) : error ? (
            <div className="channels-discover-page__status channels-discover-page__status--error">
              {error}
            </div>
          ) : !hasChannels ? (
            <div className="channels-discover-page__status">
              No channels available at the moment.
            </div>
          ) : (
            <div className="channels-discover-page__grid">
              {channels.map((channel) => (
                <div key={channel._id} className="channels-discover-page__grid-item">
                  <ChannelSliderCard
                    channel={channel}
                    onVisit={() => setPreviewChannel(channel)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </AppNavigationPage>
      {previewChannel && (
        <SubscriptionPreview
          channelId={previewChannel._id}
          ownerUsername={previewChannel.owner_username}
          onClose={() => setPreviewChannel(null)}
        />
      )}
    </>
  );
}
